import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Grid,
  Typography,
  Box,
  Button,
  Container,
  ListItem,
  ButtonBase,
  useMediaQuery,
} from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import BreadcrumbsBar from "./BreadcrumbsBar";
import UndrawJobHunt from "icons/undraw_job-hunt_5umi.svg";
import AuthenticationModal from "bundles/Candidate/components/AuthenticationModal";
import BlogPostCard from "bundles/common/components/BlogPostCard";
import sh from "bundles/common/utils/sh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FakeCV from "pictures/CvExample.png";
import FakeCVSmall from "pictures/CvExampleSmall.png";
import {
  ARCHITECTURE_CATEGORIES,
  ENGINEERING_CATEGORIES,
  CONSTRUCTION_CATEGORIES,
  ADMINISTRATION_CATEGORIES,
} from "bundles/common/utils/categoryData";

const useStyles = makeStyles(theme => ({
  band: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  illustration: {
    width: "100%",
    maxWidth: 300,
    height: "auto",
    margin: "0 auto",
    display: "block",
  },
  list: {
    paddingLeft: "20px",
  },
  blogList: {
    maxWidth: 600,
    margin: "24px auto",
    paddingLeft: 20,
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  generateCvBox: {
    width: "80%",
    height: "auto",
    backgroundImage: "radial-gradient(circle, #3F4B71, #2A334E)",
    position: "relative",
    padding: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
    border: "8px solid transparent",
    "&:hover": {
      border: "8px solid #079344",
      boxSizing: "border-box",
    },
    "&:hover $cvImageStyle": {
      transform: "scale(1.08)",
    },
  },
  cvWrapper: {
    position: "relative",
    width: "100%",
    height: "400px",
  },
  cvImageStyle: {
    position: "absolute",
    top: "-10%",
    bottom: "-10%",
    right: "8%",
    width: "auto",
    height: "126%",
    border: "0.4px solid lightgrey",
    transition: "transform .2s ease-in-out",
  },
  customCvButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#079344 !important",
    },
    "&:hover $textContainer": {
      marginRight: "0px",
    },
    "&:hover $arrowIcon": {
      opacity: 1,
    },
  },
  textContainer: {
    transition: "margin-right 0.3s",
    marginRight: "-32px",
  },
  arrowIcon: {
    opacity: 0,
    marginLeft: "8px",
    transition: "opacity 0.3s",
  },
}));

const resolveCategoryLabel = (industry, slug, locale, decap = false) => {
  const MAP = {
    architecture : ARCHITECTURE_CATEGORIES,
    engineering : ENGINEERING_CATEGORIES,
    construction : CONSTRUCTION_CATEGORIES,
    administration : ADMINISTRATION_CATEGORIES,
  };

  const list  = MAP[industry]?.[locale] || MAP[industry]?.en || [];
  const match = list.find(c => c.slug === slug);

  let label = match ? match.label : slug.replace(/-/g, " ");

  if (decap && !/^[A-Z]{2,}$/.test(label)) {
    label = label.toLocaleLowerCase(locale);
  }

  return label;
};

const NoJobsAvailable = () => {
  const classes = useStyles();
  const theme = useTheme();
  const intl = useIntl();
  const i18nLocale = useSelector(state => state.i18nLocale);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const currentCandidate = useSelector(state => state.current_candidate);
  const { category } = useParams();
  const industry = window.location.pathname.split("/")[3];
  const categoryLabel = resolveCategoryLabel(
    industry,
    category,
    i18nLocale,
    true
  );
  const capIndustry = industry.charAt(0).toUpperCase() + industry.slice(1);
  const capCategoryLabel = resolveCategoryLabel(
    industry,
    category,
    i18nLocale
  );
  const [isLoginModalOpened, setIsLoginModalOpened] = useState(false);
  const [loginToAddResume, setLoginToAddResume] = useState(false);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [squarehubId, setSquarehubId] = useState(null);
  const authenticityToken = useSelector(state => state.authenticity_token);

  let sectionCount = 0;
  const nextBg = () => (sectionCount++ % 2 ? "#F7F8FC" : "white" );

  const industryButtons = React.useMemo(
    () => [
      {
        slug: "architecture",
        label: intl.formatMessage(
          { id: "company_industry.architecture", defaultMessage: "Architecture" }
        ),
      },
      {
        slug: "engineering",
        label: intl.formatMessage(
          { id: "company_industry.engineering", defaultMessage: "Engineering" }
        ),
      },
      {
        slug: "construction",
        label: intl.formatMessage(
          { id: "company_industry.construction", defaultMessage: "Construction" }
        ),
      },
      {
        slug: "administration",
        label: intl.formatMessage(
          { id: "company_industry.administration", defaultMessage: "Administration" }
        ),
      },
    ],
    [intl]
  );

  useEffect(() => {
    // Fetch blog posts
    sh.get("search/blog_posts", {
      params: {
        authenticity_token: authenticityToken,
        filters: { resource_type: "candidate" },
      },
    })
      .then(res => {
        const posts = res.data.data?.blog_posts.slice(0, 4);
        setBlogPosts(posts);
      })
      .catch(err => {
        console.error(err);
      });

    // Fetch Squarehub ID
    sh.get(`/${i18nLocale}/find_squarehub_id`)
      .then(response => {
        if (response?.data?.id) setSquarehubId(response.data.id);
      })
      .catch(err => console.error(err));
  }, [i18nLocale, authenticityToken]);

  useEffect(() => {
    if (currentCandidate && currentTarget) {
      window.location.href = currentTarget;
    }
  }, [currentCandidate, currentTarget]);

  const handleCreateResumeClicked = e => {
    e.preventDefault();
    if (currentCandidate) {
      window.location.href = `/${i18nLocale}/candidate/settings?selectedSection=resume_generator`;
    } else {
      setLoginToAddResume(true);
      setCurrentTarget(
        `/${i18nLocale}/candidate/settings?selectedSection=resume_generator`
      );
      setIsLoginModalOpened(true);
    }
  };

  const onAuthenticationModalClosed = () => {
    setIsLoginModalOpened(false);
    setTimeout(() => setLoginToAddResume(false), 200);
  };

  const crumbs = [
    { label: "Home", path: `/${i18nLocale}` },
    { label: "Job Offers", path: `/${i18nLocale}/job-offers` },
    { label: capIndustry, path: `/${i18nLocale}/job-offers/${industry}` },
    { label: capCategoryLabel, current: true },
  ];

  return (
    <>
      {/* ---------- block_1 : (title + CTA) ------------------------- */}
      <Box sx={{ backgroundColor: nextBg() }} className={classes.band}>
        <Container maxWidth="lg">
          <BreadcrumbsBar crumbs={crumbs} />

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                align="left"
                sx={{ mt: 4, mb: 3, fontSize: "2.5rem" }}
              >
                <FormattedMessage
                  id="public_job_offer_index.no_jobs_available.title"
                  values={{ category: categoryLabel }}
                />
              </Typography>

              <Typography
                variant="body1"
                align="left"
                sx={{ maxWidth: 720, mb: 4 }}
              >
                <FormattedMessage id="public_job_offer_index.first_block_intro" />
              </Typography>

              <Box textAlign="left">
                <Button
                  variant="contained"
                  color="primary"
                  href={`/${i18nLocale}/job-offers`}
                >
                  <FormattedMessage id="public_job_offer_index.no_jobs_available.view_all_jobs" />
                </Button>
              </Box>
            </Grid>

            {isDesktop &&
              <Grid item xs={12} md={6}>
                <img
                  src={UndrawJobHunt}
                  alt="Job hunt illustration"
                  className={classes.illustration}
                />
              </Grid>
            }
          </Grid>
        </Container>
      </Box>

      {/* ---------- block 2 : industry chooser ---------- */}
      <Box sx={{ backgroundColor: nextBg() }} className={classes.band}>
        {/* flex wrapper that owns the fixed height */}
        <Box
          sx={{
            height: isDesktop ? "45vh" : "25vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h2"
              align="center"
              sx={{ mb: 6 }}
            >
              <FormattedMessage id="candidate_search.sectors" />
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              {industryButtons.map(({ slug, label }) => (
                <Grid item xs={6} md={3} key={slug}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    href={`/${i18nLocale}/job-offers/${slug}`}
                    sx={{ p: 2 }}
                  >
                    {label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>


      {/* ---------- block_3 : (generic description of industries) ------------ */}
      <Box sx={{ backgroundColor: nextBg() }} className={`${classes.band}`}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            gutterBottom
            style={{ textAlign: "left", fontSize: "2rem" }}
          >
            <FormattedMessage
              id="public_job_offer_index.no_jobs_available.what_professionals_do"
              values={{ industry: capIndustry }}
            />
          </Typography>

          <Typography variant="body1" paragraph style={{ textAlign: "left" }}>
            <FormattedMessage
              id="public_job_offer_index.no_jobs_available.professionals_description"
              values={{ industry: capIndustry }}
            />
          </Typography>

          <Typography variant="h3" gutterBottom style={{ textAlign: "left" }}>
            <FormattedMessage id="public_job_offer_index.no_jobs_available.job_responsibilities" />
          </Typography>

          <ul className={classes.list} style={{ textAlign: "left" }}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(responsibilityNumber => (
              <li key={responsibilityNumber}>
                <FormattedMessage
                  id={`public_job_offer_index.no_jobs_available.industry_skills.${industry}.responsibilities.${responsibilityNumber}`}
                />
              </li>
            ))}
          </ul>

          <Typography variant="h3" gutterBottom style={{ textAlign: "left" }}>
            <FormattedMessage id="public_job_offer_index.no_jobs_available.core_skills" />
          </Typography>

          <ul className={classes.list} style={{ textAlign: "left" }}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(skillNumber => (
              <li key={skillNumber}>
                <FormattedMessage
                  id={`public_job_offer_index.no_jobs_available.industry_skills.${industry}.skills.${skillNumber}`}
                />
              </li>
            ))}
          </ul>
        </Container>
      </Box>

      {/* ---------- block_4 : (CV creation tool) -------------------- */}
      {isDesktop ?
        <Box sx={{ backgroundColor: nextBg() }} className={classes.band}>
          <Container maxWidth="sm" sx={{ textAlign: "center" }}>
            <Typography variant="h2" gutterBottom style={{ fontSize: "2rem" }}>
              <FormattedMessage id="public_job_offer_index.no_jobs_available.need_cv" />
            </Typography>

            <Typography variant="body1" paragraph>
              <FormattedMessage id="public_job_offer_index.no_jobs_available.cv_description" />
            </Typography>

            <ButtonBase
              style={{ width: "100%" }}
              onClick={handleCreateResumeClicked}
            >
              <Box className={classes.generateCvBox}>
                <Grid container>
                  <Grid item xs={6} style={{ padding: "40px 0 40px 80px" }}>
                    <Typography
                      variant="h2"
                      style={{
                        color: "white",
                        marginBottom: "22px",
                        textAlign: "left",
                        fontSize: "32px",
                        fontWeight: "bold",
                      }}
                    >
                      <FormattedMessage id="public_job_offer_index.no_jobs_available.need_cv" />
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{
                        color: "white",
                        marginBottom: "16px",
                        textAlign: "left",
                      }}
                    >
                      <FormattedMessage id="public_job_offer_index.no_jobs_available.cv_description" />
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <Button
                        variant="rounded"
                        color="primaryContained"
                        size="medium"
                        className={classes.customCvButton}
                        style={{ marginTop: "16px", width: "250px" }}
                        onClick={handleCreateResumeClicked}
                      >
                        <Box className={classes.textContainer}>
                          <FormattedMessage id="public_job_offer_index.no_jobs_available.try_cv_tool" />
                        </Box>
                        <ArrowForwardIcon className={classes.arrowIcon} />
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={6} style={{ marginTop: "0" }}>
                    <Box className={classes.cvWrapper}>
                      <img
                        src={FakeCV}
                        srcSet={`${FakeCV} 1x, ${FakeCVSmall} 0.5x`}
                        alt={intl.formatMessage({
                          id: "landing_candidate.generate_cv.alt",
                        })}
                        className={classes.cvImageStyle}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </ButtonBase>
          </Container>
        </Box>
        :
        <Box sx={{ backgroundColor: nextBg() }}>
          <Grid
            container
            justify="center"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "white",
              padding: "60px 0px 40px",
            }}
          >
            <ButtonBase style={{ width: "100%" }} onClick={handleCreateResumeClicked}>
              <Box
                className={classes.generateCvBox}
                style={{ marginTop: "10px" }}
              >
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    style={{ padding: "20px 20px" }}
                  >
                    <Typography
                      variant="h2"
                      style={{
                        color: "white",
                        marginBottom: "22px",
                        textAlign: "center",
                        fontSize: "32px",
                        fontWeight: "bold",
                      }}
                    >
                      <FormattedMessage id="landing_candidate.generate_cv.title" />
                    </Typography>
                    <Typography
                      variant="body1"
                      style={{ color: "white", marginBottom: "16px", textAlign: "left" }}
                    >
                      <FormattedMessage id="landing_candidate.generate_cv.description" />
                    </Typography>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      style={{ marginBottom: "16px" }}
                    >
                      <ListItem style={{ padding: "0" }}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: "bold", color: "white" }}
                        >
                          ✓{" "}
                          <FormattedMessage id="landing_candidate.generate_cv.point_1" />
                        </Typography>
                      </ListItem>
                      <ListItem style={{ padding: "0" }}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: "bold", color: "white" }}
                        >
                          ✓{" "}
                          <FormattedMessage id="landing_candidate.generate_cv.point_2" />
                        </Typography>
                      </ListItem>
                      <ListItem style={{ padding: "0" }}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: "bold", color: "white" }}
                        >
                          ✓{" "}
                          <FormattedMessage id="landing_candidate.generate_cv.point_3" />
                        </Typography>
                      </ListItem>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="center"
                    >
                      <Button
                        variant="rounded"
                        color="primaryContained"
                        size="small"
                        className={classes.customCvButton}
                        style={{ marginTop: "16px", width: "250px" }}
                        onClick={handleCreateResumeClicked}
                      >
                        <Box className={classes.textContainer}>
                          <FormattedMessage id="landing_candidate.generate_cv.call_to_action" />
                        </Box>
                        <ArrowForwardIcon className={classes.arrowIcon} />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </ButtonBase>
          </Grid>
        </Box>
      }

      {/* ---------- block_4 : (latest blog posts) -------------------- */}
      <Box sx={{ backgroundColor: nextBg() }} className={`${classes.band}`}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            style={{ marginBottom: "50px", fontSize: "2rem" }}
          >
            <FormattedMessage id="public_job_offer_index.no_jobs_available.latest_blog" />
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {blogPosts.map(post => (
              <Grid item xs={12} sm={6} md={3} key={post.id}>
                <BlogPostCard
                  post={post}
                  companyId={squarehubId}
                  landing={true}
                />
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="center" marginTop="48px">
            <Button
              variant="rounded"
              color="primaryContained"
              style={{ minWidth: "300px" }}
              href={`/${i18nLocale}/candidates/posts`}
            >
              <FormattedMessage id="landing_candidate.view_our_advises" />
            </Button>
          </Box>
        </Container>
      </Box>

      <AuthenticationModal
        isOpen={isLoginModalOpened}
        section="login_or_signin"
        closeModal={onAuthenticationModalClosed}
        addResumeLogin={loginToAddResume}
      />
    </>
  );
};

export default NoJobsAvailable;
